import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { PrivacyPolicy } from './PrivacyPolicy';
import { CookieConsent } from './CookieConsent';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './components/ui/alert-dialog';
import { toast, Toaster } from 'sonner';
import { ShoppingCart, Menu, X, Phone, Mail, MapPin, Trash2, Plus, Edit, CheckCircle2 } from 'lucide-react';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cart Context
const CartContext = createContext();

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success('Prodotto aggiunto al carrello');
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) return;
    setCart(prev => prev.map(item => 
      item.id === productId ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => setCart([]);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

// Scroll to Top Component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Header Component
const Header = () => {
  const { cart } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <header className="sticky top-0 z-50 bg-white border-b shadow-sm header-3d">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center logo-3d" data-testid="logo-link" onClick={() => setMobileMenuOpen(false)}>
            <img 
              src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/7le0kepu_Copia%20di%20partnership%20demmalight.png" 
              alt="EdiliziaMia Logo" 
              className="h-16 w-auto"
              style={{maxWidth: '250px'}}
            />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="nav-link" data-testid="nav-home">Home</Link>
            <Link to="/chi-siamo" className="nav-link" data-testid="nav-about">Chi Siamo</Link>
            <Link to="/consulenza" className="nav-link" data-testid="nav-consulting">Consulenza</Link>
            <Link to="/shop" className="nav-link" data-testid="nav-shop">Shop</Link>
            <Link to="/contatti" className="nav-link" data-testid="nav-contact">Contatti</Link>
            <Link to="/carrello" className="relative" data-testid="cart-link">
              <ShoppingCart className="w-6 h-6 text-gray-700 hover:text-primary transition" />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center cart-badge-3d" data-testid="cart-count">
                  {totalItems}
                </span>
              )}
            </Link>
          </div>

          <button className="md:hidden z-50" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu Sidebar from Right */}
        <div className={`mobile-menu-sidebar ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
          <div className="p-6 space-y-4">
            <Link to="/" className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
              <span>Home</span>
            </Link>
            <Link to="/chi-siamo" className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
              <span>Chi Siamo</span>
            </Link>
            <Link to="/consulenza" className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
              <span>Consulenza</span>
            </Link>
            <Link to="/shop" className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
              <span>Shop</span>
            </Link>
            <Link to="/contatti" className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
              <span>Contatti</span>
            </Link>
            <Link to="/carrello" className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
              <span>Carrello ({totalItems})</span>
            </Link>
            <Link to="/privacy-policy" className="mobile-menu-item text-sm text-gray-600" onClick={() => setMobileMenuOpen(false)}>
              <span>Privacy Policy</span>
            </Link>
          </div>
        </div>

        {/* Overlay */}
        {mobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
        )}
      </nav>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white mt-20">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/7le0kepu_Copia%20di%20partnership%20demmalight.png" 
                alt="EdiliziaMia Logo" 
                className="h-12 w-auto mb-2"
              />
            </div>
            <p className="text-gray-400">Materiali edili di qualità per professionisti e privati.</p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4">Link Veloci</h3>
            <div className="space-y-2">
              <Link to="/chi-siamo" className="block text-gray-400 hover:text-white transition">Chi Siamo</Link>
              <Link to="/consulenza" className="block text-gray-400 hover:text-white transition">Consulenza</Link>
              <Link to="/shop" className="block text-gray-400 hover:text-white transition">Shop</Link>
              <Link to="/contatti" className="block text-gray-400 hover:text-white transition">Contatti</Link>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Contatti</h3>
            <div className="space-y-3 text-gray-400">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>351 018 4747</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>ediliziamia@gmail.com</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Via Ponte Bloy 10, Caltanissetta</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2025 EdiliziaMia. Tutti i diritti riservati.</p>
          <div className="flex justify-center gap-6 mt-3 flex-wrap">
            <a href="https://www.facebook.com/MiaEdilizia/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition">
              Seguici su Facebook
            </a>
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div>
      <section className="hero-section-large" data-testid="hero-section">
        <div className="hero-overlay">
          <div className="container mx-auto px-4 text-center">
            <h1 className="hero-title" data-testid="hero-title">EdiliziaMia - Il Tuo Partner per l'Edilizia</h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              Da oltre 20 anni forniamo materiali edili di qualità superiore a Caltanissetta e provincia. 
              Dalla piccola ristrutturazione al grande cantiere, siamo il punto di riferimento per professionisti e privati.
            </p>
            <div className="flex gap-4 justify-center mt-8">
              <Button size="lg" className="hero-button" onClick={() => navigate('/shop')} data-testid="hero-cta-button">
                Scopri i Prodotti
              </Button>
              <Button size="lg" variant="outline" className="hero-button-secondary" onClick={() => navigate('/contatti')} data-testid="hero-contact-button">
                Richiedi Preventivo
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="section-title" data-testid="about-section-title">Chi Siamo</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mt-4">
              EdiliziaMia è il punto di riferimento per materiali edili di qualità a Caltanissetta. 
              Con una vasta gamma di prodotti selezionati dai migliori produttori italiani ed europei, 
              serviamo professionisti del settore e privati con la stessa dedizione e competenza.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            <Card className="stat-card text-center" data-testid="stat-card-1">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary mb-2">20+</div>
                <div className="text-gray-600 font-medium">Anni di Esperienza</div>
              </CardContent>
            </Card>

            <Card className="stat-card text-center" data-testid="stat-card-2">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary mb-2">27</div>
                <div className="text-gray-600 font-medium">Prodotti Gipsos</div>
              </CardContent>
            </Card>

            <Card className="stat-card text-center" data-testid="stat-card-3">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary mb-2">1000+</div>
                <div className="text-gray-600 font-medium">Clienti Soddisfatti</div>
              </CardContent>
            </Card>

            <Card className="stat-card text-center" data-testid="stat-card-4">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary mb-2">24h</div>
                <div className="text-gray-600 font-medium">Consegna Express</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-title" data-testid="services-title">I Nostri Servizi</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <Card className="service-card" data-testid="service-card-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  Materiali di Alta Qualità
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  Selezioniamo esclusivamente materiali certificati dai migliori produttori italiani. 
                  Ogni prodotto è testato per garantire prestazioni ottimali e durabilità nel tempo. 
                  Lavoriamo con marchi leader come Gipsos, Weber, Mapei e molti altri.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card" data-testid="service-card-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  Consegna Rapida e Puntuale
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  Consegna a domicilio su Caltanissetta e provincia con servizio standard (2-3 giorni) 
                  o express (24 ore). Disponiamo di mezzi attrezzati per trasportare materiali pesanti 
                  e ingombranti direttamente nel vostro cantiere.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card" data-testid="service-card-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  Consulenza Tecnica Specializzata
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  Il nostro team di esperti è sempre disponibile per consigliarvi nella scelta dei materiali 
                  più adatti. Supporto tecnico per calcolo quantità, compatibilità prodotti e soluzioni 
                  per problematiche specifiche di cantiere.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="categories-title">Le Nostre Categorie</h2>
            <p className="text-lg text-gray-600 mt-4">Esplora la nostra vasta gamma di prodotti per l'edilizia</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="category-preview-card cursor-pointer hover:shadow-xl transition-all" onClick={() => navigate('/shop')}>
              <div className="aspect-video overflow-hidden rounded-t-lg bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/3szuu9ud_Immagine%202025-11-12%20183040.png" 
                  alt="Intonaci base cemento Gipsos" 
                  className="w-full h-full object-contain p-4" 
                />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl">Intonaci e Rasanti</CardTitle>
                <CardDescription className="text-base">
                  17 prodotti professionali Gipsos: Precem, Rasocem, Tonacem, Rasokoll, Rasabeton e altri. 
                  Intonaci premiscelati, rasanti fibrati, finiture bianche e grigie per interni ed esterni.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="category-preview-card cursor-pointer hover:shadow-xl transition-all" onClick={() => navigate('/shop')}>
              <div className="aspect-video overflow-hidden rounded-t-lg bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/tpg2gxbo_Immagine%202025-11-12%20183147.png" 
                  alt="Prodotti base gesso Gipsos" 
                  className="w-full h-full object-contain p-4" 
                />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl">Prodotti Base Gesso</CardTitle>
                <CardDescription className="text-base">
                  10 prodotti professionali Gipsos: Pregip, Premac, Intofin, Rasofin, Rasostuck, Scagliola e Gesso da Muro. 
                  Per interni, applicazioni manuali e a macchina, rasature e finiture di alta qualità.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button size="lg" onClick={() => navigate('/shop')} data-testid="view-all-products-button">
              Vedi Tutti i Prodotti
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="gallery-title">La Nostra Sede a Caltanissetta</h2>
            <p className="text-lg text-gray-600 mt-4">Vieni a trovarci nel nostro punto vendita e magazzino</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="overflow-hidden group cursor-pointer">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/fcrd7yli_Immagine%20WhatsApp%202025-06-27%20ore%2016.14.07_af715685.jpg" 
                  alt="EdiliziaMia - Esterno del punto vendita"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="pt-4">
                <h3 className="font-semibold text-center">Il Nostro Punto Vendita</h3>
                <p className="text-sm text-gray-600 text-center mt-2">Via Ponte Bloy 10, Caltanissetta</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden group cursor-pointer">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/pwq5q0sb_Immagine%20WhatsApp%202025-06-27%20ore%2016.14.10_aa9e3515.jpg" 
                  alt="EdiliziaMia - Magazzino materiali edili"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="pt-4">
                <h3 className="font-semibold text-center">Il Nostro Magazzino</h3>
                <p className="text-sm text-gray-600 text-center mt-2">Ampia disponibilità di prodotti sempre in stock</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden group cursor-pointer">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/uw0kcztk_Immagine%20WhatsApp%202025-06-27%20ore%2016.14.10_d783e1e6.jpg" 
                  alt="EdiliziaMia - Interno magazzino con materiali"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="pt-4">
                <h3 className="font-semibold text-center">Scaffalature Organizzate</h3>
                <p className="text-sm text-gray-600 text-center mt-2">Gestione efficiente per consegne rapide</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-700 mb-6">
              Disponiamo di oltre 500m² di magazzino con un vasto assortimento di materiali edili sempre disponibili. 
              Il nostro team è pronto ad accoglierti per consigliarti nella scelta dei prodotti migliori per il tuo progetto.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                <span>Ampio parcheggio</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                <span>Carico e scarico assistito</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                <span>Personale qualificato</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="partners-title">I Nostri Partner</h2>
            <p className="text-lg text-gray-600 mt-4">Collaboriamo con i migliori marchi del settore</p>
          </div>
          
          <div className="partners-scroll-container">
            <div className="partners-scroll">
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/ql1yxx06_EDILIZIA%20MIA%20%285%29.png" 
                  alt="Fischer"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/lt5opod0_EDILIZIA%20MIA%20%286%29.png" 
                  alt="Kerakoll"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/qnf4p5qp_EDILIZIA%20MIA%20%287%29.png" 
                  alt="Kapriol"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/h29tzig1_EDILIZIA%20MIA%20%284%29.png" 
                  alt="Fassa Bortolo"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/sj8nsy99_EDILIZIA%20MIA%20%289%29.png" 
                  alt="U-Power"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/1lht9sko_EDILIZIA%20MIA%20%2810%29.png" 
                  alt="Partner"
                  className="partner-logo"
                />
              </div>

              {/* Duplicate for seamless loop */}
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/ql1yxx06_EDILIZIA%20MIA%20%285%29.png" 
                  alt="Fischer"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/lt5opod0_EDILIZIA%20MIA%20%286%29.png" 
                  alt="Kerakoll"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/qnf4p5qp_EDILIZIA%20MIA%20%287%29.png" 
                  alt="Kapriol"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/h29tzig1_EDILIZIA%20MIA%20%284%29.png" 
                  alt="Fassa Bortolo"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/sj8nsy99_EDILIZIA%20MIA%20%289%29.png" 
                  alt="U-Power"
                  className="partner-logo"
                />
              </div>
              
              <div className="partner-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/1lht9sko_EDILIZIA%20MIA%20%2810%29.png" 
                  alt="Partner"
                  className="partner-logo"
                />
              </div>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-600">
              E molti altri marchi leader nel settore edile: Gipsos, Weber, Mapei, Sika, Knauf e tanti altri
            </p>
          </div>
        </div>
      </section>

      <section 
        className="py-20"
        style={{
          backgroundColor: '#1E90FF',
          color: 'white'
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6" style={{color: 'white'}}>
            Hai bisogno di un preventivo personalizzato?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{color: 'white'}}>
            Contattaci oggi stesso per ricevere un preventivo gratuito e senza impegno. 
            Il nostro team è pronto ad aiutarti a realizzare il tuo progetto.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              className="px-8 py-4 text-lg font-semibold rounded-md transition-all duration-300 cta-button-primary"
              style={{
                backgroundColor: 'white',
                color: '#1E90FF',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}
              onClick={() => navigate('/contatti')}
              data-testid="cta-contact-button"
            >
              Contattaci Ora
            </button>
            <button
              className="px-8 py-4 text-lg font-semibold rounded-md transition-all duration-300 cta-button-secondary flex items-center gap-2"
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
              onClick={() => window.location.href = 'tel:3510184747'}
            >
              <Phone className="w-5 h-5" />
              351 018 4747
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

// Chi Siamo Page
const AboutPage = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="page-title" data-testid="about-title">Chi Siamo</h1>
      
      <div className="max-w-4xl mx-auto space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>La Nostra Storia</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              EdiliziaMia nasce dall'esperienza pluriennale nel settore dei materiali edili, con l'obiettivo di fornire prodotti di alta qualità a professionisti e privati. Con sede a Caltanissetta in Via Ponte Bloy 10, serviamo da anni imprese edili, artigiani e privati di tutta la provincia e oltre.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              La nostra passione per l'edilizia sostenibile e l'innovazione ci guida nella selezione di materiali che rispettano l'ambiente senza compromettere le prestazioni. Collaboriamo con i migliori produttori italiani ed europei come Gipsos, Weber, Mapei per garantire ai nostri clienti soluzioni all'avanguardia.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Dal piccolo progetto di ristrutturazione alla grande opera di nuova costruzione, offriamo consulenza personalizzata e prodotti di qualità superiore per ogni esigenza edile.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>La Nostra Sede</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="aspect-[4/3] overflow-hidden rounded-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/fcrd7yli_Immagine%20WhatsApp%202025-06-27%20ore%2016.14.07_af715685.jpg" 
                  alt="EdiliziaMia - Esterno"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden rounded-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/pwq5q0sb_Immagine%20WhatsApp%202025-06-27%20ore%2016.14.10_aa9e3515.jpg" 
                  alt="EdiliziaMia - Magazzino"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden rounded-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_materiali-edili/artifacts/uw0kcztk_Immagine%20WhatsApp%202025-06-27%20ore%2016.14.10_d783e1e6.jpg" 
                  alt="EdiliziaMia - Interno"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed">
              La nostra struttura di oltre 500m² comprende un ampio punto vendita e un magazzino ben organizzato con migliaia di articoli sempre disponibili. Disponiamo di comodo parcheggio e area carico/scarico per facilitare il ritiro dei materiali. Il nostro personale qualificato è sempre a disposizione per consigliarti e aiutarti nella scelta dei prodotti più adatti al tuo progetto.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>I Nostri Valori</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Qualità:</strong> Selezioniamo solo i migliori materiali certificati dai produttori leader del settore</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Sostenibilità:</strong> Attenzione all'impatto ambientale con prodotti eco-compatibili e riciclabili</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Professionalità:</strong> Assistenza tecnica qualificata e consulenza esperta per ogni esigenza di cantiere</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Affidabilità:</strong> Consegne puntuali in 24-48 ore e servizio clienti sempre disponibile</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Convenienza:</strong> Prezzi competitivi senza compromettere la qualità dei materiali</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Consulenza Page
const ConsultingPage = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="page-title" data-testid="consulting-title">Consulenza e Servizi</h1>
      
      <div className="max-w-4xl mx-auto space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Consulenza Tecnica Specializzata</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed">
              Il nostro team di esperti è a vostra disposizione per fornire consulenza tecnica qualificata nella scelta dei materiali più adatti al vostro progetto. Che si tratti di una piccola ristrutturazione o di un grande cantiere, vi aiutiamo a selezionare le soluzioni ottimali in termini di qualità, prestazioni e rapporto qualità-prezzo.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Servizio di Consegna a Domicilio</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              Offriamo un servizio di consegna rapido ed efficiente direttamente in cantiere o presso il vostro domicilio. Il nostro obiettivo è garantire che i materiali arrivino nei tempi concordati, permettendovi di rispettare le tempistiche del progetto.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Consegna standard: 3-5 giorni lavorativi</li>
              <li>• Consegna espressa: 24-48 ore</li>
              <li>• Consegna urgente: Entro 24 ore (su disponibilità)</li>
              <li>• Trasporto con mezzi attrezzati per materiali pesanti</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Preventivi Personalizzati</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              Richiedi un preventivo gratuito e senza impegno per il tuo progetto. Analizziamo le tue esigenze e ti forniamo un'offerta dettagliata comprensiva di:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Lista completa dei materiali necessari</li>
              <li>• Specifiche tecniche di ogni prodotto</li>
              <li>• Quantità ottimali per evitare sprechi</li>
              <li>• Tempi di consegna stimati</li>
              <li>• Supporto tecnico per la posa in opera</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assistenza Post-Vendita</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed">
              La nostra assistenza non termina con la consegna. Siamo disponibili per supportarvi durante l'utilizzo dei materiali, fornire chiarimenti sulle modalità di posa e risolvere eventuali problematiche. Il vostro successo è il nostro successo.
            </p>
          </CardContent>
        </Card>

        <div className="text-center mt-12">
          <Button size="lg" asChild data-testid="contact-cta-button">
            <Link to="/contatti">Richiedi una Consulenza</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

// Shop Page
const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei prodotti');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category_id === selectedCategory);

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="page-title" data-testid="shop-title">I Nostri Prodotti</h1>
      
      <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
        <Button
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          onClick={() => setSelectedCategory('all')}
          data-testid="category-filter-all"
        >
          Tutti
        </Button>
        {categories.map(cat => (
          <Button
            key={cat.id}
            variant={selectedCategory === cat.id ? 'default' : 'outline'}
            onClick={() => setSelectedCategory(cat.id)}
            data-testid={`category-filter-${cat.slug}`}
          >
            {cat.name}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20" data-testid="loading-indicator">Caricamento...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map(product => (
            <Card key={product.id} className="product-card" data-testid={`product-card-${product.id}`}>
              <div className="aspect-[3/4] overflow-hidden rounded-t-lg bg-white p-4">
                <img 
                  src={product.images[0] || 'https://via.placeholder.com/400x300'} 
                  alt={product.name}
                  className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                  data-testid={`product-image-${product.id}`}
                />
              </div>
              <CardHeader className="pt-4">
                <div className="text-sm text-primary font-medium mb-1" data-testid={`product-category-${product.id}`}>
                  {product.category_name}
                </div>
                <CardTitle className="text-lg leading-tight" data-testid={`product-name-${product.id}`}>{product.name}</CardTitle>
                <CardDescription className="line-clamp-2 text-sm" data-testid={`product-description-${product.id}`}>
                  {product.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 pt-2">
                <Button 
                  className="w-full" 
                  variant="outline" 
                  asChild
                  data-testid={`product-details-button-${product.id}`}
                >
                  <Link to={`/prodotto/${product.id}`}>Vedi Dettagli</Link>
                </Button>
                <Button 
                  className="w-full" 
                  onClick={() => addToCart(product)}
                  data-testid={`add-to-cart-button-${product.id}`}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Aggiungi al Carrello
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && filteredProducts.length === 0 && (
        <div className="text-center py-20 text-gray-500" data-testid="no-products">Nessun prodotto disponibile</div>
      )}
    </div>
  );
};

// Product Detail Page
const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento del prodotto');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-16 text-center" data-testid="loading-product">Caricamento...</div>;
  }

  if (!product) return null;

  return (
    <div className="container mx-auto px-4 py-16">
      <Button variant="outline" onClick={() => navigate('/shop')} className="mb-8" data-testid="back-to-shop-button">
        ← Torna allo Shop
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <img 
            src={product.images[0] || 'https://via.placeholder.com/600x400'} 
            alt={product.name}
            className="w-full h-auto object-contain"
            data-testid="product-detail-image"
            style={{maxHeight: '600px'}}
          />
        </div>

        <div className="space-y-6">
          <div>
            <div className="text-sm text-primary font-medium mb-2" data-testid="product-detail-category">
              {product.category_name}
            </div>
            <h1 className="text-4xl font-bold mb-4" data-testid="product-detail-name">{product.name}</h1>
            <p className="text-gray-700 leading-relaxed text-lg" data-testid="product-detail-description">
              {product.description}
            </p>
          </div>

          <Card className="bg-gray-50">
            <CardHeader>
              <CardTitle className="text-xl">Specifiche Tecniche</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed" data-testid="product-detail-specs">
                {product.technical_specs}
              </pre>
            </CardContent>
          </Card>

          <div className="space-y-3 pt-4">
            <button 
              className="w-full text-lg py-6 px-6 rounded-md font-semibold flex items-center justify-center gap-2 transition-all duration-300 add-to-cart-button"
              style={{
                backgroundColor: '#1E90FF',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 12px rgba(30, 144, 255, 0.3)'
              }}
              onClick={() => {
                addToCart(product);
                navigate('/carrello');
              }}
              data-testid="add-to-cart-detail-button"
            >
              <ShoppingCart className="w-6 h-6" />
              Aggiungi al Carrello e Richiedi Preventivo
            </button>
            
            <button
              className="w-full text-lg py-6 px-6 rounded-md font-semibold transition-all duration-300 back-to-shop-button"
              style={{
                backgroundColor: 'white',
                color: '#1E90FF',
                border: '2px solid #1E90FF'
              }}
              onClick={() => navigate('/shop')}
              data-testid="back-to-shop-bottom-button"
            >
              Torna allo Shop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cart Page
const CartPage = () => {
  const { cart, removeFromCart, updateQuantity } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center" data-testid="empty-cart">
        <ShoppingCart className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold mb-4">Il tuo carrello è vuoto</h2>
        <Button onClick={() => navigate('/shop')} data-testid="continue-shopping-button">Continua gli Acquisti</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="page-title" data-testid="cart-title">Carrello</h1>

      <div className="max-w-4xl mx-auto space-y-4">
        {cart.map(item => (
          <Card key={item.id} data-testid={`cart-item-${item.id}`}>
            <CardContent className="flex items-center gap-4 p-6">
              <img 
                src={item.images[0] || 'https://via.placeholder.com/100'} 
                alt={item.name}
                className="w-20 h-20 object-cover rounded"
                data-testid={`cart-item-image-${item.id}`}
              />
              <div className="flex-1">
                <h3 className="font-semibold" data-testid={`cart-item-name-${item.id}`}>{item.name}</h3>
                <p className="text-sm text-gray-600" data-testid={`cart-item-category-${item.id}`}>{item.category_name}</p>
              </div>
              <div className="flex items-center gap-3">
                <Input 
                  type="number" 
                  min="1" 
                  value={item.quantity}
                  onChange={(e) => updateQuantity(item.id, parseInt(e.target.value))}
                  className="w-20"
                  data-testid={`cart-item-quantity-${item.id}`}
                />
                <Button 
                  variant="destructive" 
                  size="icon"
                  onClick={() => removeFromCart(item.id)}
                  data-testid={`remove-from-cart-button-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        <div className="flex justify-end gap-4 mt-8">
          <Button variant="outline" onClick={() => navigate('/shop')} data-testid="continue-shopping-cart-button">
            Continua gli Acquisti
          </Button>
          <Button onClick={() => navigate('/checkout')} data-testid="proceed-to-checkout-button">
            Procedi al Checkout
          </Button>
        </div>
      </div>
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    phone: '',
    email: '',
    urgency: 'settimana-prossima'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const items = cart.map(item => ({
        product_id: item.id,
        product_name: item.name,
        quantity: item.quantity
      }));

      await axios.post(`${API}/quotes`, {
        ...formData,
        items
      });

      toast.success('Richiesta preventivo inviata con successo!');
      clearCart();
      navigate('/');
    } catch (error) {
      toast.error('Errore nell\'invio della richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    navigate('/carrello');
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="page-title" data-testid="checkout-title">Richiesta Preventivo</h1>

      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>I Tuoi Dati</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="first_name">Nome *</Label>
                <Input 
                  id="first_name"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  data-testid="checkout-first-name"
                />
              </div>

              <div>
                <Label htmlFor="last_name">Cognome *</Label>
                <Input 
                  id="last_name"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  data-testid="checkout-last-name"
                />
              </div>

              <div>
                <Label htmlFor="company">Azienda (opzionale)</Label>
                <Input 
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  data-testid="checkout-company"
                />
              </div>

              <div>
                <Label htmlFor="phone">Numero di Telefono *</Label>
                <Input 
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="checkout-phone"
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="checkout-email"
                />
              </div>

              <div>
                <Label htmlFor="urgency">Urgenza Preventivo *</Label>
                <Select value={formData.urgency} onValueChange={(val) => setFormData({...formData, urgency: val})}>
                  <SelectTrigger data-testid="checkout-urgency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oggi">Oggi</SelectItem>
                    <SelectItem value="domani">Domani</SelectItem>
                    <SelectItem value="dopo-domani">Dopo Domani</SelectItem>
                    <SelectItem value="settimana-prossima">Settimana Prossima</SelectItem>
                    <SelectItem value="non-urgente">Non Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-quote-button">
                {submitting ? 'Invio in corso...' : 'Invia Richiesta Preventivo'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Riepilogo Prodotti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cart.map(item => (
                <div key={item.id} className="flex justify-between items-center py-2 border-b" data-testid={`checkout-item-${item.id}`}>
                  <div className="flex-1">
                    <div className="font-medium" data-testid={`checkout-item-name-${item.id}`}>{item.name}</div>
                    <div className="text-sm text-gray-600">Quantità: {item.quantity}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">
                Il nostro team ti contatterà entro 24 ore con un preventivo dettagliato per i prodotti selezionati.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Messaggio inviato con successo!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="page-title" data-testid="contact-title">Contattaci</h1>

      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Invia un Messaggio</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input 
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  data-testid="contact-name"
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="contact-email"
                />
              </div>

              <div>
                <Label htmlFor="phone">Telefono</Label>
                <Input 
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="contact-phone"
                />
              </div>

              <div>
                <Label htmlFor="message">Messaggio *</Label>
                <Textarea 
                  id="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  data-testid="contact-message"
                />
              </div>

              <Button type="submit" className="w-full" disabled={submitting} data-testid="contact-submit-button">
                {submitting ? 'Invio in corso...' : 'Invia Messaggio'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informazioni di Contatto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-primary mt-1" />
                <div>
                  <div className="font-medium">Telefono</div>
                  <div className="text-gray-600">351 018 4747</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-1" />
                <div>
                  <div className="font-medium">Email</div>
                  <div className="text-gray-600">ediliziamia@gmail.com</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-1" />
                <div>
                  <div className="font-medium">Indirizzo</div>
                  <div className="text-gray-600">Via Ponte Bloy 10<br />Caltanissetta, Italy</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Orari di Apertura</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Lunedì - Venerdì</span>
                <span className="font-medium">8:00 - 18:00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sabato</span>
                <span className="font-medium">8:00 - 13:00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Domenica</span>
                <span className="font-medium">Chiuso</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Admin Login
const AdminLogin = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem('admin_token', response.data.access_token);
      toast.success('Login effettuato!');
      navigate('/wpadmin/dashboard');
    } catch (error) {
      toast.error('Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Admin Login</CardTitle>
          <CardDescription className="text-center">Accedi al pannello amministrativo</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input 
                id="username"
                required
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                data-testid="admin-username"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password"
                type="password"
                required
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                data-testid="admin-password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-button">
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/wpadmin');
      return;
    }
    fetchData();
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
  });

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, quotesRes, contactsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/quotes`, getAuthHeaders()),
        axios.get(`${API}/admin/contacts`, getAuthHeaders())
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setQuotes(quotesRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/wpadmin');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/wpadmin');
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/admin/products/${id}`, getAuthHeaders());
      toast.success('Prodotto eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/admin/categories/${id}`, getAuthHeaders());
      toast.success('Categoria eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const deleteQuote = async (id) => {
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, getAuthHeaders());
      toast.success('Preventivo eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" data-testid="admin-dashboard-title">Pannello Amministrativo</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPasswordForm(true)} data-testid="change-password-button">
              Cambia Password
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-button">Logout</Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="products" data-testid="tab-products">Prodotti ({products.length})</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categorie ({categories.length})</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">Preventivi ({quotes.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contatti ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Gestione Prodotti</h2>
              <Button onClick={() => { setEditingProduct(null); setShowProductForm(true); }} data-testid="add-product-button">
                <Plus className="w-4 h-4 mr-2" /> Nuovo Prodotto
              </Button>
            </div>

            <div className="grid gap-4">
              {products.map(product => (
                <Card key={product.id} data-testid={`admin-product-${product.id}`}>
                  <CardContent className="flex items-center gap-4 p-4">
                    <img src={product.images[0]} alt={product.name} className="w-16 h-16 object-cover rounded" />
                    <div className="flex-1">
                      <h3 className="font-semibold">{product.name}</h3>
                      <p className="text-sm text-gray-600">{product.category_name}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setEditingProduct(product); setShowProductForm(true); }} data-testid={`edit-product-${product.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive" data-testid={`delete-product-${product.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
                            <AlertDialogDescription>Questa azione eliminerà il prodotto definitivamente.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteProduct(product.id)}>Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="categories" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Gestione Categorie</h2>
              <Button onClick={() => setShowCategoryForm(true)} data-testid="add-category-button">
                <Plus className="w-4 h-4 mr-2" /> Nuova Categoria
              </Button>
            </div>

            <div className="grid gap-4">
              {categories.map(category => (
                <Card key={category.id} data-testid={`admin-category-${category.id}`}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <h3 className="font-semibold">{category.name}</h3>
                      <p className="text-sm text-gray-600">{category.slug}</p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="destructive" data-testid={`delete-category-${category.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
                          <AlertDialogDescription>Questa azione eliminerà la categoria definitivamente.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteCategory(category.id)}>Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="quotes" className="space-y-4">
            <h2 className="text-xl font-semibold">Richieste Preventivi</h2>
            <div className="grid gap-4">
              {quotes.map(quote => (
                <Card key={quote.id} data-testid={`admin-quote-${quote.id}`}>
                  <CardHeader>
                    <CardTitle>{quote.first_name} {quote.last_name} {quote.company && `- ${quote.company}`}</CardTitle>
                    <CardDescription>
                      Email: {quote.email} | Tel: {quote.phone} | Urgenza: {quote.urgency}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <h4 className="font-medium">Prodotti richiesti:</h4>
                      <ul className="list-disc list-inside">
                        {quote.items.map((item, idx) => (
                          <li key={idx}>{item.product_name} - Quantità: {item.quantity}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-4">
                      <Button size="sm" variant="destructive" onClick={() => deleteQuote(quote.id)} data-testid={`delete-quote-${quote.id}`}>
                        <Trash2 className="w-4 h-4 mr-2" /> Elimina
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="space-y-4">
            <h2 className="text-xl font-semibold">Messaggi Contatti</h2>
            <div className="grid gap-4">
              {contacts.map(contact => (
                <Card key={contact.id} data-testid={`admin-contact-${contact.id}`}>
                  <CardHeader>
                    <CardTitle>{contact.name}</CardTitle>
                    <CardDescription>
                      Email: {contact.email} {contact.phone && `| Tel: ${contact.phone}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{contact.message}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {showProductForm && (
        <ProductFormModal 
          product={editingProduct} 
          categories={categories}
          onClose={() => { setShowProductForm(false); setEditingProduct(null); }} 
          onSuccess={() => { setShowProductForm(false); setEditingProduct(null); fetchData(); }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {showCategoryForm && (
        <CategoryFormModal 
          onClose={() => setShowCategoryForm(false)} 
          onSuccess={() => { setShowCategoryForm(false); fetchData(); }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {showPasswordForm && (
        <PasswordFormModal 
          onClose={() => setShowPasswordForm(false)}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
};

// Product Form Modal
const ProductFormModal = ({ product, categories, onClose, onSuccess, getAuthHeaders }) => {
  const [formData, setFormData] = useState(product || {
    name: '',
    category_id: '',
    category_name: '',
    description: '',
    technical_specs: '',
    images: ['']
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const selectedCat = categories.find(c => c.id === formData.category_id);
      const data = { ...formData, category_name: selectedCat.name };

      if (product) {
        await axios.put(`${API}/admin/products/${product.id}`, data, getAuthHeaders());
        toast.success('Prodotto aggiornato');
      } else {
        await axios.post(`${API}/admin/products`, data, getAuthHeaders());
        toast.success('Prodotto creato');
      }
      onSuccess();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>{product ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nome Prodotto</Label>
              <Input id="name" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="product-form-name" />
            </div>

            <div>
              <Label htmlFor="category">Categoria</Label>
              <Select value={formData.category_id} onValueChange={(val) => setFormData({...formData, category_id: val})}>
                <SelectTrigger data-testid="product-form-category">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea id="description" required rows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} data-testid="product-form-description" />
            </div>

            <div>
              <Label htmlFor="technical_specs">Specifiche Tecniche</Label>
              <Textarea id="technical_specs" required rows={5} value={formData.technical_specs} onChange={(e) => setFormData({...formData, technical_specs: e.target.value})} data-testid="product-form-specs" />
            </div>

            <div>
              <Label htmlFor="image">URL Immagine</Label>
              <Input id="image" type="url" required value={formData.images[0] || ''} onChange={(e) => setFormData({...formData, images: [e.target.value]})} data-testid="product-form-image" />
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} data-testid="product-form-submit">
                {submitting ? 'Salvataggio...' : 'Salva'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} data-testid="product-form-cancel">Annulla</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Category Form Modal
const CategoryFormModal = ({ onClose, onSuccess, getAuthHeaders }) => {
  const [formData, setFormData] = useState({ name: '', slug: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/admin/categories`, formData, getAuthHeaders());
      toast.success('Categoria creata');
      onSuccess();
    } catch (error) {
      toast.error('Errore nella creazione');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>Nuova Categoria</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="cat_name">Nome Categoria</Label>
              <Input id="cat_name" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="category-form-name" />
            </div>

            <div>
              <Label htmlFor="slug">Slug (es: malte-tecniche)</Label>
              <Input id="slug" required value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} data-testid="category-form-slug" />
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} data-testid="category-form-submit">
                {submitting ? 'Creazione...' : 'Crea'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} data-testid="category-form-cancel">Annulla</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Password Form Modal
const PasswordFormModal = ({ onClose, getAuthHeaders }) => {
  const [formData, setFormData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Le password non corrispondono');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      }, getAuthHeaders());
      toast.success('Password modificata con successo');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella modifica');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>Cambia Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="current_password">Password Corrente</Label>
              <Input id="current_password" type="password" required value={formData.current_password} onChange={(e) => setFormData({...formData, current_password: e.target.value})} data-testid="password-form-current" />
            </div>

            <div>
              <Label htmlFor="new_password">Nuova Password</Label>
              <Input id="new_password" type="password" required value={formData.new_password} onChange={(e) => setFormData({...formData, new_password: e.target.value})} data-testid="password-form-new" />
            </div>

            <div>
              <Label htmlFor="confirm_password">Conferma Nuova Password</Label>
              <Input id="confirm_password" type="password" required value={formData.confirm_password} onChange={(e) => setFormData({...formData, confirm_password: e.target.value})} data-testid="password-form-confirm" />
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} data-testid="password-form-submit">
                {submitting ? 'Modifica...' : 'Modifica Password'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} data-testid="password-form-cancel">Annulla</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

function App() {
  return (
    <CartProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App">
          <Routes>
            <Route path="/wpadmin" element={<AdminLogin />} />
            <Route path="/wpadmin/dashboard" element={<AdminDashboard />} />
            <Route path="*" element={
              <>
                <Header />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/chi-siamo" element={<AboutPage />} />
                  <Route path="/consulenza" element={<ConsultingPage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/prodotto/:id" element={<ProductDetailPage />} />
                  <Route path="/carrello" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/contatti" element={<ContactPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                </Routes>
                <Footer />
                <CookieConsent />
              </>
            } />
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;